import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { MGMT } from "../env";
import { useAuthApi } from "../hooks/useAuthApi";
import type { ChargerGroup, FirmwareBinary, FirmwarePushResponse } from "../types";
import { useToast } from "../toast";

interface Props {
  open: boolean;
  group: ChargerGroup | null;
  onClose: () => void;
}

export default function PushFirmwareDialog({ open, group, onClose }: Props) {
  const api = useAuthApi();
  const toast = useToast();
  const [firmwares, setFirmwares] = useState<FirmwareBinary[]>([]);
  const [selectedFirmwareId, setSelectedFirmwareId] = useState<number | "">("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<FirmwarePushResponse | null>(null);

  const loadFirmwares = useCallback(async () => {
    if (!open) return;
    try {
      const data = await api<FirmwareBinary[]>(`${MGMT}/firmware-binaries`);
      setFirmwares(data ?? []);
      setSelectedFirmwareId((current) => current || (data?.[0]?.id ?? ""));
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  }, [api, open, toast]);

  useEffect(() => {
    loadFirmwares();
  }, [loadFirmwares]);

  const pushFirmware = async () => {
    if (!group || !selectedFirmwareId) {
      toast.show("Select a firmware version", "error");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const pushed = await api<FirmwarePushResponse>(`${MGMT}/firmware-binaries/push`, {
        method: "POST",
        body: JSON.stringify({
          charger_group_id: group.id,
          firmware_binary_id: selectedFirmwareId,
        }),
      });
      if (!pushed) throw new Error("No response returned");
      setResult(pushed);
      toast.show("Firmware push dispatched");
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Push Firmware{group ? `: ${group.name}` : ""}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Select a firmware version to push to all chargers in this group.
          </Typography>
          <FormControl fullWidth required>
            <InputLabel>Firmware Version</InputLabel>
            <Select
              value={selectedFirmwareId}
              label="Firmware Version"
              onChange={(e) => setSelectedFirmwareId(e.target.value as number)}
            >
              {firmwares.map((fw) => (
                <MenuItem key={fw.id} value={fw.id}>
                  {fw.version} • {fw.file_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {firmwares.length === 0 && (
            <Alert severity="info">No firmware binaries available. Add one in the backend first.</Alert>
          )}
          {result && (
            <Alert severity="info">
              Dispatched {result.dispatched}/{result.total_targets}
              {result.offline.length > 0 ? ` • Offline: ${result.offline.join(", ")}` : ""}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={pushFirmware}
          disabled={busy || !group || !selectedFirmwareId || firmwares.length === 0}
        >
          Push Firmware
        </Button>
      </DialogActions>
    </Dialog>
  );
}
